/**
 * additional-tables/api — katalog tabel dodatkowych (§4.10 CLAUDE.md), listing
 * dla konsumentów poza kreatorem modułu (np. klocek `additional_view` w
 * `LayoutElementRegistry`). Mirror `lib/additional-views/api.ts` — ten sam
 * kształt `parentEntity`/`parentTableName`, filtrowane client-side przez
 * `filterAdditionalTablesForModule` (`document-module-slugs.ts`).
 *
 * Katalog czytają ten sam hook (`useAdditionalTablesList`) m.in. `AdditionalTableConfigPanel`
 * w `SystemModuleBuilderPage.tsx` i klocek `additional_view`; `AdditionalTableTab` ma własne,
 * inline `useQuery` pod tym samym kluczem (`['additional-tables']`) i tym samym endpointem,
 * więc react-query dzieli cache między wszystkimi ścieżkami.
 */
import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';

import { additionalFieldsKey, fetchAdditionalTableFields } from './parent-field-values';
import type { AdditionalField } from './types';

export const ADDITIONAL_TABLES_LIST_KEY = ['additional-tables'] as const;

export interface AdditionalTableMeta {
  id: string;
  slug: string;
  displayName: string;
  parentEntity: string;
  allowAddRow: boolean;
}

export function useAdditionalTablesList() {
  return useQuery<AdditionalTableMeta[]>({
    queryKey: ADDITIONAL_TABLES_LIST_KEY,
    queryFn: async () => {
      const res = await api.get<{ tables: AdditionalTableMeta[] }>('/settings/additional-tables');
      return res.data.tables;
    },
    staleTime: 60_000,
  });
}

/** Stan odczytu definicji pól tabeli dodatkowej (`useAdditionalTableFields`). */
export type AdditionalTableFieldsStatus = 'pending' | 'error' | 'notFound' | 'ready';

export interface AdditionalTableFieldsResult {
  status: AdditionalTableFieldsStatus;
  /** Pola tabeli; pusta tablica, dopóki `status` ≠ `'ready'`. */
  fields: AdditionalField[];
  /** Błąd zapytania (katalogu albo pól) — tylko przy `status === 'error'`. */
  error?: unknown;
}

const NO_FIELDS: AdditionalField[] = [];

/**
 * Definicje pól tabeli dodatkowej po slugu: katalog tabel (`useAdditionalTablesList`) →
 * id tabeli → pola (`GET /settings/additional-tables/:id/fields`). Pola idą pod tym samym
 * kluczem cache co w `AdditionalTableTab`, więc zakładka na karcie rekordu i konsument
 * spoza niej (np. edytor układu kolumn w kreatorze) dzielą jedno zapytanie. Ustawienia →
 * Tabele dodatkowe (`AdditionalTablesTab`) czytają i odświeżają ten sam klucz, więc zmiana
 * pola w ustawieniach od razu trafia do otwartego kreatora.
 *
 * `notFound` = katalog załadowany, ale brak w nim tabeli o tym slugu (albo slug pusty).
 * `error` tylko wtedy, gdy zapytanie nie ma żadnych danych: nieudane odświeżenie w tle przy
 * dostępnych danych z cache nie psuje wyniku (`ready` zostaje, jak w `AdditionalTableTab`).
 */
export function useAdditionalTableFields(
  tableSlug: string | undefined,
): AdditionalTableFieldsResult {
  const tablesQuery = useAdditionalTablesList();
  const tableId =
    tableSlug === undefined || tableSlug === ''
      ? undefined
      : tablesQuery.data?.find((table) => table.slug === tableSlug)?.id;

  const fieldsQuery = useQuery<AdditionalField[]>({
    queryKey: additionalFieldsKey(tableId ?? ''),
    queryFn: () => {
      if (tableId === undefined) {
        // `enabled` poniżej nie dopuszcza tej ścieżki — jawny błąd zamiast zapytania o pusty id.
        return Promise.reject(new Error('useAdditionalTableFields: brak id tabeli dodatkowej'));
      }
      return fetchAdditionalTableFields(tableId);
    },
    enabled: tableId !== undefined,
  });

  // Dane z cache mają pierwszeństwo przed `isError`: nieudane odświeżenie w tle (przy
  // dostępnych danych) nie zamienia gotowego wyniku w błąd.
  if (tablesQuery.data === undefined) {
    return tablesQuery.isError
      ? { status: 'error', fields: NO_FIELDS, error: tablesQuery.error }
      : { status: 'pending', fields: NO_FIELDS };
  }
  if (tableId === undefined) return { status: 'notFound', fields: NO_FIELDS };
  if (fieldsQuery.data === undefined) {
    return fieldsQuery.isError
      ? { status: 'error', fields: NO_FIELDS, error: fieldsQuery.error }
      : { status: 'pending', fields: NO_FIELDS };
  }
  return { status: 'ready', fields: fieldsQuery.data };
}
